import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Booking, { BookingWrapper } from '@spike/booking-model';
import clsx from 'clsx';
import BlockedTime from 'components/Calendar3/BlockedTime';
import CreateBooking from 'components/CreateBooking';
import { Moment } from 'moment';
import { FunctionComponent, useState } from 'react';
import SidePanel from './SidePanel';
import { AppointmentOrderItem, Order } from '@spike/order-model';
import ConfirmClosePopup from 'components/CreateBooking/UI/ConfirmClosePopup';
import PetCard from 'components/PetCard';
import { fetchPetsThunk } from '@spike/pets-action';
import { useDispatch, useSelector } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import { AppointmentMultiple } from 'model/NewMultipleBooking';
import { EvaluationReport } from '@spike/report-model';
import Staff from 'model/Staff';
import type { RootState } from 'store';
import { OrdersStatus } from '@spike/orders-action';
import { NewBookingsStatus } from '@spike/new-bookings-action';

interface CreateBookingDrawerProps {
    fromClient?: boolean;
    bookingReschedule?: BookingWrapper;
    staffId?: number;
    at?: Moment;
    parentID?: number;
    petID?: number;
    parentName?: string;
    appointmentId?: number;
    detailView?: boolean;
    clientAdded?: boolean;
    petAdded?: boolean;
    bookingRebook?: Booking;
    className?: string;
    blockedEdit?: boolean;
    petsLoaded?: boolean;
    order?: Order;
    appointmentItem?: AppointmentOrderItem;
    multipleAppointments?: Array<AppointmentMultiple>;
    isEvaluation?: boolean;
    isDaycareFastCheckIn?: boolean;
    onAddClient?: () => void;
    onAddPet?: (clientId: number) => void;
    onSave?: () => void;
    onClose: () => void;
    onBooked: () => void;
    onStartBooking?: () => void;
    blockCalendarId?: number;
    flow?: 'grooming' | 'daycare' | 'boarding' | 'transportation' | 'walking' | 'training';
    showTabs?: boolean;
    onChangeTime?: (
        bookedAt: Moment | undefined,
        duration: number | undefined,
        staffId: number | undefined,
        show: boolean
    ) => void;
    onRemoveClient?: () => void;
    onAddEvaluation?: (petId: number, clientId: number) => void;
    onViewResults?: (evaluationReport?: EvaluationReport) => void;
    onRefreshBooking?: () => void;
    blockedTimeStaffOptions?: Array<Staff>;
    initialMembershipSelection?: import('components/CreateBooking/Memberships/UI/MembershipsSelector/MembershipsSelector').MembershipsSelection;
    initialMembershipServiceId?: number;
    zIndex?: number;
    /** Set when the booking converts a waitlist request. */
    waitlistId?: number;
    /** Location where the new booking will be created. Defaults to the logged-in location. */
    marketplaceId?: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            'display': 'flex',
            'height': '100%',
            'flexDirection': 'column',
            '& .MuiDrawer-paperAnchorRight': {
                width: '100%',
                [theme.breakpoints.up('md')]: {
                    width: '590px !important'
                }
            }
        },
        hidePanel: {
            display: 'none'
        }
    })
);

export const CreateBookingDrawer: FunctionComponent<CreateBookingDrawerProps> = props => {
    const classes = useStyles();

    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const hideCloseButton =
        newBookingsStatus === NewBookingsStatus.Saving ||
        newBookingsStatus === NewBookingsStatus.SavingForDeposit ||
        newBookingsStatus === NewBookingsStatus.SavingMultiple ||
        newBookingsStatus === NewBookingsStatus.SavingMultipleBookingForDeposit ||
        ordersStatus === OrdersStatus.SavingOrderRedeem;

    const [showNewAppointment, setShowNewAppoinment] = useState(props.blockCalendarId === undefined);
    const [showPopUpExit, setShowPopUpExit] = useState(false);
    const [changeNewBooking, setChangeNewBooking] = useState(false);
    const [showPetCard, setShowPetCard] = useState<{ petId: number | undefined; clientId: number | undefined }>({
        petId: undefined,
        clientId: undefined
    });

    const changeTimeHandler = (bookedAt?: Moment, duration?: number, staffId?: number) => {
        props.onChangeTime &&
            props.onChangeTime(bookedAt, duration, staffId, showNewAppointment || props.blockCalendarId === undefined);
    };

    const handlerClose = (value?: boolean) => {
        if (changeNewBooking && !value) {
            setShowPopUpExit(true);
        } else {
            props.onClose();
        }
    };

    const refreshHandler = () => {
        dispatch(fetchPetsThunk(apiClientWrapper, showPetCard.clientId!));
        setShowPetCard(prev => ({ ...prev, petId: undefined, clientId: undefined }));
    };

    const confirmCloseDialog = (
        <ConfirmClosePopup
            title="Exit Booking"
            question="Your changes will not be saved. Are you sure you want to exit this booking?"
            onDiscard={() => setShowPopUpExit(false)}
            onSave={props.onClose}
        />
    );

    return (
        <>
            <SidePanel
                side="right"
                disableEnforceFocus
                showCloseButton={!hideCloseButton}
                onClose={() => handlerClose(undefined)}
                className={clsx(classes.container, props.className, { [classes.hidePanel]: showPetCard.petId })}
                zIndex={props.zIndex}
            >
                {showNewAppointment ? (
                    <CreateBooking
                        fromClient={props.fromClient}
                        staffId={props.staffId}
                        at={props.at}
                        parentID={props.parentID}
                        petID={props.petID}
                        parentName={props.parentName}
                        appointmentId={props.appointmentId}
                        detailView={props.detailView}
                        clientAdded={props.clientAdded}
                        petAdded={props.petAdded}
                        order={props.order}
                        appointmentItem={props.appointmentItem}
                        waitlistId={props.waitlistId}
                        marketplaceId={props.marketplaceId}
                        multipleAppointments={props.multipleAppointments}
                        isEvaluation={props.isEvaluation}
                        flow={props.flow}
                        isDaycareFastCheckIn={props.isDaycareFastCheckIn}
                        onAddClient={props.onAddClient}
                        onAddPet={props.onAddPet}
                        onSave={props.onClose}
                        onClose={handlerClose}
                        onBooked={props.onBooked}
                        onChangeTime={changeTimeHandler}
                        onShowBlockedTime={props.showTabs ? () => setShowNewAppoinment(false) : undefined}
                        onRemoveClient={props.onRemoveClient}
                        onChangeNewBooking={(value: boolean) => setChangeNewBooking(value)}
                        onShowPetCard={(petId: number, clientId: number) =>
                            setShowPetCard({ petId: petId, clientId: clientId })
                        }
                        onAddEvaluation={props.onAddEvaluation}
                        onRefreshBooking={props.onRefreshBooking}
                        onViewResults={props.onViewResults}
                        initialMembershipSelection={props.initialMembershipSelection}
                        initialMembershipServiceId={props.initialMembershipServiceId}
                    />
                ) : (
                    <BlockedTime
                        from={props.at?.clone()}
                        to={props.at?.clone().add(15, 'minutes')}
                        staffId={props.staffId}
                        blockCalendarId={props.blockCalendarId}
                        staffOptions={props.blockedTimeStaffOptions}
                        onClose={props.onClose}
                        onBlocked={props.onClose}
                        onShowBooking={() => setShowNewAppoinment(true)}
                        onChangeTime={changeTimeHandler}
                        blockedEdit={props.blockedEdit}
                    />
                )}
                {showPopUpExit && confirmCloseDialog}
            </SidePanel>
            {showPetCard.petId && (
                <PetCard
                    petId={showPetCard.petId}
                    optionMenuSelected="vaccines"
                    onSaved={refreshHandler}
                    onClose={refreshHandler}
                />
            )}
        </>
    );
};

export default CreateBookingDrawer;
